import { useCallback, useRef, useState } from 'react';
import {
    useGetWarrantyAuditDocumentsByIdQuery,
    useUploadWarrantyAuditDocumentMutation
} from '../../../../../../api/endpoints/warrantyAudit';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../../../api/endpoints/documentContent';
import DocumentDownloadService from '../../../../../../utils/documentDownloadService';
import toastService from '../../../../../../utils/toastService';
import { useLanguage } from '../../../../../../contexts/LanguageContext';

export const ACCEPTED_AUDIT_FILE_TYPES = {
    'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/plain': ['.txt']
};

const getDocumentValue = (doc, camelKey, pascalKey) => doc?.[camelKey] ?? doc?.[pascalKey];

const useAuditDocuments = (auditResultId) => {
    const { th } = useLanguage();
    const [newFiles, setNewFiles] = useState([]);
    const [isProcessingFiles, setIsProcessingFiles] = useState(false);
    const documentService = useRef(new DocumentDownloadService());

    const [uploadDocument] = useUploadWarrantyAuditDocumentMutation();
    const [triggerDownload] = useLazyDownloadDocumentByKeyQuery();

    const {
        data: documents = [],
        isLoading: isLoadingDocuments,
        refetch: refetchDocuments
    } = useGetWarrantyAuditDocumentsByIdQuery(auditResultId, {
        skip: !auditResultId
    });

    const uploadPendingFiles = useCallback(
        async (targetAuditResultId = auditResultId) => {
            if (!targetAuditResultId || newFiles.length === 0) return true;

            setIsProcessingFiles(true);

            let successCount = 0;
            let failCount = 0;

            for (const file of newFiles) {
                try {
                    await uploadDocument({
                        warrantyAuditResultId: targetAuditResultId,
                        file,
                        description: file.name || 'Garanti Denetim Dosyası'
                    }).unwrap();

                    successCount += 1;
                } catch (error) {
                    console.error('Garanti denetim dosyası yükleme hatası:', file.name, error);
                    failCount += 1;
                }
            }

            setIsProcessingFiles(false);

            if (failCount > 0) {
                toastService.warn(
                    `${successCount} dosya yüklendi, ${failCount} dosya yüklenemedi.`,
                    th('preApproval.fileOperations.partialSuccess') || 'Kısmi başarılı'
                );
            } else {
                toastService.success(
                    th('preApproval.fileOperations.uploadSuccess')?.replace('{0}', successCount) ||
                        `${successCount} dosya yüklendi.`,
                    th('preApproval.fileOperations.success') || 'Başarılı'
                );
            }

            setNewFiles([]);
            refetchDocuments?.();

            return failCount === 0;
        },
        [auditResultId, newFiles, refetchDocuments, th, uploadDocument]
    );

    const handleDownloadDocument = useCallback(
        async (documentData) => {
            try {
                await documentService.current.downloadByKey(
                    triggerDownload,
                    getDocumentValue(documentData, 'fileKey', 'FileKey'),
                    documentData,
                    {
                        successMessage: 'Dosya başarıyla indirildi.',
                        errorMessage: 'Dosya indirilemedi.'
                    }
                );
            } catch (error) {
                console.error('Garanti denetim dosyası indirme hatası:', error);
            }
        },
        [triggerDownload]
    );

    const normalizedDocuments = Array.isArray(documents)
        ? documents.reduce((acc, doc) => {
              const description = getDocumentValue(doc, 'description', 'Description');
              const title = getDocumentValue(doc, 'title', 'Title');
              const storedFileName = getDocumentValue(doc, 'fileName', 'FileName');
              const displayName = description || title || storedFileName;
              const fileKey = getDocumentValue(doc, 'fileKey', 'FileKey');

              if (!displayName && !fileKey) return acc;

              acc.push({
                  ...doc,
                  id: getDocumentValue(doc, 'id', 'Id'),
                  fileKey,
                  storedFileName,
                  fileName: displayName || fileKey,
                  description,
                  title,
                  displayName: displayName || fileKey
              });

              return acc;
          }, [])
        : [];

    return {
        documents: normalizedDocuments,
        isLoadingDocuments,
        newFiles,
        setNewFiles,
        isProcessingFiles,
        uploadPendingFiles,
        handleDownloadDocument
    };
};

export default useAuditDocuments;